import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNavigation from '../../components/feature/TopNavigation';
import BottomNavigation from '../../components/feature/BottomNavigation';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';

interface CaseStatus {
  id: string;
  title: string;
  date: string;
  completed: boolean;
  current?: boolean;
}

interface MedicalCase {
  id: string;
  caseNumber: string;
  type: string;
  doctor: string;
  center: string;
  date: string;
  status: 'pending' | 'in-progress' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high';
  timeline: CaseStatus[];
  notes?: string;
  nextAppointment?: string;
}

const mockCases: MedicalCase[] = [
  {
    id: '1',
    caseNumber: 'TK-2024-001',
    type: 'استشارة طبية',
    doctor: 'د. أحمد محمد الشيخ',
    center: 'مستشفى الملك فهد',
    date: '2024-01-15',
    status: 'in-progress',
    priority: 'high',
    nextAppointment: '2024-01-22',
    timeline: [
      { id: '1', title: 'تم تسجيل الحالة', date: '2024-01-15 09:00', completed: true },
      { id: '2', title: 'قيد المراجعة الطبية', date: '2024-01-15 10:30', completed: true },
      { id: '3', title: 'تحت المعالجة', date: '2024-01-16 14:00', completed: true, current: true },
      { id: '4', title: 'في انتظار النتائج', date: '', completed: false },
      { id: '5', title: 'مكتملة', date: '', completed: false }
    ],
    notes: 'يرجى الصيام 12 ساعة قبل الموعد القادم لإجراء التحاليل المطلوبة'
  },
  {
    id: '2',
    caseNumber: 'TK-2024-002',
    type: 'تحاليل مختبرية',
    doctor: 'د. فاطمة أحمد النور',
    center: 'مجمع عيادات النور الطبي',
    date: '2024-01-10',
    status: 'completed',
    priority: 'medium',
    timeline: [
      { id: '1', title: 'تم تسجيل الحالة', date: '2024-01-10 08:00', completed: true },
      { id: '2', title: 'قيد المراجعة الطبية', date: '2024-01-10 09:15', completed: true },
      { id: '3', title: 'تحت المعالجة', date: '2024-01-11 11:00', completed: true },
      { id: '4', title: 'تم الانتهاء من التحاليل', date: '2024-01-12 16:30', completed: true },
      { id: '5', title: 'مكتملة', date: '2024-01-12 17:00', completed: true }
    ]
  },
  {
    id: '3',
    caseNumber: 'TK-2024-003',
    type: 'أشعة تشخيصية',
    doctor: 'د. محمد عبدالله الحسن',
    center: 'مستشفى الدكتور سليمان الحبيب',
    date: '2024-01-20',
    status: 'pending',
    priority: 'low',
    timeline: [
      { id: '1', title: 'تم تسجيل الحالة', date: '2024-01-20 14:00', completed: true, current: true },
      { id: '2', title: 'قيد المراجعة الطبية', date: '', completed: false },
      { id: '3', title: 'تحت المعالجة', date: '', completed: false },
      { id: '4', title: 'في انتظار النتائج', date: '', completed: false },
      { id: '5', title: 'مكتملة', date: '', completed: false }
    ]
  }
];

export default function TrackingPage() {
  const navigate = useNavigate();
  const [cases] = useState<MedicalCase[]>(mockCases);
  const [selectedCase, setSelectedCase] = useState<MedicalCase | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'pending' | 'in-progress' | 'completed'>('all');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-50';
      case 'in-progress': return 'text-blue-600 bg-blue-50';
      case 'completed': return 'text-green-600 bg-green-50';
      case 'cancelled': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'قيد الانتظار';
      case 'in-progress': return 'قيد المعالجة';
      case 'completed': return 'مكتملة';
      case 'cancelled': return 'ملغية';
      default: return 'غير محدد';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'low': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getPriorityText = (priority: string) => {
    switch (priority) {
      case 'high': return 'عالية';
      case 'medium': return 'متوسطة';
      case 'low': return 'منخفضة';
      default: return 'غير محدد';
    }
  };

  const filteredCases = cases.filter(caseItem => {
    if (activeTab === 'all') return true;
    return caseItem.status === activeTab;
  });

  const renderTimeline = (timeline: CaseStatus[]) => (
    <div className="space-y-4">
      {timeline.map((status, index) => (
        <div key={status.id} className="flex items-start gap-3">
          <div className="flex flex-col items-center">
            <div className={`w-4 h-4 rounded-full border-2 ${
              status.completed 
                ? 'bg-[#34C759] border-[#34C759]' 
                : status.current
                ? 'bg-[#007AFF] border-[#007AFF]'
                : 'bg-white border-gray-300'
            }`}>
              {status.completed && (
                <i className="ri-check-line text-white text-xs flex items-center justify-center w-full h-full"></i>
              )}
            </div>
            {index < timeline.length - 1 && (
              <div className={`w-0.5 h-8 mt-1 ${
                status.completed ? 'bg-[#34C759]' : 'bg-gray-200'
              }`}></div>
            )}
          </div>
          <div className="flex-1 pb-2">
            <h4 className={`font-medium ${
              status.completed ? 'text-[#333333]' : status.current ? 'text-[#007AFF]' : 'text-[#7A7A7A]'
            }`}>
              {status.title}
            </h4>
            {status.date && (
              <p className="text-sm text-[#7A7A7A] mt-1">
                {new Date(status.date).toLocaleDateString('ar-SA', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  if (selectedCase) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TopNavigation 
          title="تفاصيل الحالة" 
          showBack 
          onBack={() => setSelectedCase(null)}
        />
        
        <div className="pt-20 pb-24 px-4">
          {/* Case Header */}
          <Card padding="md" className="mb-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-lg font-bold text-[#333333] mb-1">{selectedCase.type}</h2>
                <p className="text-[#7A7A7A] text-sm">رقم الحالة: {selectedCase.caseNumber}</p>
              </div>
              <div className="flex gap-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedCase.status)}`}>
                  {getStatusText(selectedCase.status)}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(selectedCase.priority)}`}>
                  {getPriorityText(selectedCase.priority)}
                </span>
              </div>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <i className="ri-user-line text-[#7A7A7A]"></i>
                <span className="text-[#7A7A7A]">الطبيب:</span>
                <span className="font-medium">{selectedCase.doctor}</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-hospital-line text-[#7A7A7A]"></i>
                <span className="text-[#7A7A7A]">المركز:</span>
                <span className="font-medium">{selectedCase.center}</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-calendar-line text-[#7A7A7A]"></i>
                <span className="text-[#7A7A7A]">تاريخ التسجيل:</span>
                <span className="font-medium">
                  {new Date(selectedCase.date).toLocaleDateString('ar-SA')}
                </span>
              </div>
              {selectedCase.nextAppointment && (
                <div className="flex items-center gap-2">
                  <i className="ri-calendar-check-line text-[#007AFF]"></i>
                  <span className="text-[#7A7A7A]">الموعد القادم:</span>
                  <span className="font-medium text-[#007AFF]">
                    {new Date(selectedCase.nextAppointment).toLocaleDateString('ar-SA')}
                  </span>
                </div>
              )}
            </div>
          </Card>

          {/* Timeline */}
          <Card padding="md" className="mb-6">
            <h3 className="font-bold text-[#333333] mb-4">الجدول الزمني للحالة</h3>
            {renderTimeline(selectedCase.timeline)}
          </Card>

          {/* Notes */}
          {selectedCase.notes && (
            <Card padding="md" className="mb-6 bg-[#007AFF]/5">
              <h3 className="font-bold text-[#333333] mb-2">ملاحظات مهمة</h3>
              <p className="text-[#333333] text-sm">{selectedCase.notes}</p>
            </Card>
          )}

          {/* Actions */}
          <div className="space-y-3">
            <Button variant="primary" size="lg" className="w-full">
              <i className="ri-download-line text-lg ml-2"></i>
              تحميل التقرير الطبي
            </Button>
            <Button variant="outline" size="lg" className="w-full">
              <i className="ri-message-line text-lg ml-2"></i>
              إرسال رسالة للطبيب
            </Button>
            {selectedCase.nextAppointment && (
              <Button variant="outline" size="lg" className="w-full">
                <i className="ri-calendar-line text-lg ml-2"></i>
                تعديل الموعد القادم
              </Button>
            )}
          </div>
        </div>
        
        <BottomNavigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TopNavigation 
        title="متابعة الحالات" 
        showBack 
        onBack={() => navigate('/')}
      />
      
      <div className="pt-20 pb-24 px-4">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#333333] mb-2">حالاتي الطبية</h1>
          <p className="text-[#7A7A7A] text-sm">متابعة جميع الحالات والمواعيد الطبية</p>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {[
            { key: 'all', label: 'الكل', count: cases.length },
            { key: 'pending', label: 'قيد الانتظار', count: cases.filter(c => c.status === 'pending').length },
            { key: 'in-progress', label: 'قيد المعالجة', count: cases.filter(c => c.status === 'in-progress').length },
            { key: 'completed', label: 'مكتملة', count: cases.filter(c => c.status === 'completed').length }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                activeTab === tab.key
                  ? 'bg-[#007AFF] text-white'
                  : 'bg-white text-[#7A7A7A] border border-gray-200'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* Cases List */}
        <div className="space-y-4">
          {filteredCases.map((caseItem) => (
            <Card
              key={caseItem.id}
              padding="md"
              onClick={() => setSelectedCase(caseItem)}
              className="cursor-pointer hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-bold text-[#333333] mb-1">{caseItem.type}</h3>
                  <p className="text-[#7A7A7A] text-sm">رقم الحالة: {caseItem.caseNumber}</p>
                </div>
                <div className="flex gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(caseItem.status)}`}>
                    {getStatusText(caseItem.status)}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(caseItem.priority)}`}>
                    {getPriorityText(caseItem.priority)}
                  </span>
                </div>
              </div>
              
              <div className="space-y-1 text-sm mb-3">
                <div className="flex items-center gap-2">
                  <i className="ri-user-line text-[#7A7A7A] text-xs"></i>
                  <span className="text-[#7A7A7A]">{caseItem.doctor}</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-hospital-line text-[#7A7A7A] text-xs"></i>
                  <span className="text-[#7A7A7A]">{caseItem.center}</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-calendar-line text-[#7A7A7A] text-xs"></i>
                  <span className="text-[#7A7A7A]">
                    {new Date(caseItem.date).toLocaleDateString('ar-SA')}
                  </span>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-[#7A7A7A]">التقدم</span>
                  <span className="text-xs font-medium text-[#333333]">
                    {Math.round((caseItem.timeline.filter(t => t.completed).length / caseItem.timeline.length) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-[#34C759] h-2 rounded-full transition-all"
                    style={{
                      width: `${(caseItem.timeline.filter(t => t.completed).length / caseItem.timeline.length) * 100}%`
                    }}
                  ></div>
                </div>
              </div>

              {caseItem.nextAppointment && (
                <div className="flex items-center gap-2 text-sm text-[#007AFF]">
                  <i className="ri-calendar-check-line"></i>
                  <span>الموعد القادم: {new Date(caseItem.nextAppointment).toLocaleDateString('ar-SA')}</span>
                </div>
              )}
            </Card>
          ))}
        </div>

        {filteredCases.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-file-list-line text-4xl text-gray-300 mb-4"></i>
            <h3 className="text-lg font-medium text-[#7A7A7A] mb-2">لا توجد حالات</h3>
            <p className="text-sm text-[#7A7A7A]">لم يتم العثور على حالات في هذه الفئة</p>
          </div>
        )}
      </div>
      
      <BottomNavigation />
    </div>
  );
}
